import React from "react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Step,
  AnnotatedScreen,
  InfoBox,
  WarningBox,
  TipBox,
  Quiz,
} from "@/tutorial/components/TutorialComponents";

const DOC_TABLE = [
  {
    type: "Loi de Finances",
    examples: "PLFI annuel, loi de finances rectificative",
    fields: "Recettes, dépenses, déficit, macros pays, devise",
  },
  {
    type: "Budget de l'État",
    examples: "Budget général, budgets annexes",
    fields: "Lignes budgétaires par secteur, masses salariales",
  },
  {
    type: "Rapport Cour des Comptes",
    examples: "Rapport annuel, rapports particuliers",
    fields: "Observations, infractions, recommandations",
  },
  {
    type: "Contrat d'emprunt / dette",
    examples: "Émissions obligataires, prêts bilatéraux",
    fields: "Principal, taux, maturité, charge annuelle",
  },
  {
    type: "Code pénal / loi anticorruption",
    examples: "Code pénal, lois spéciales",
    fields: "Articles, qualifications, peines applicables",
  },
];

export default function Chapter2() {
  return (
    <ChapterShell id={2}>
      <Section id="section-2-1" eyebrow="2.1" title="Quels documents uploader ?">
        <p>
          VITAE-PUBLICA™ accepte plusieurs catégories de documents. Mieux le
          type est renseigné, plus l'extraction est fine.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-muted/60 text-left">
                <th className="border border-border px-3 py-2 font-semibold">Type</th>
                <th className="border border-border px-3 py-2 font-semibold">Exemples</th>
                <th className="border border-border px-3 py-2 font-semibold">Données extraites</th>
              </tr>
            </thead>
            <tbody>
              {DOC_TABLE.map((r) => (
                <tr key={r.type} className="align-top">
                  <td className="border border-border px-3 py-2 font-medium">{r.type}</td>
                  <td className="border border-border px-3 py-2 text-muted-foreground">{r.examples}</td>
                  <td className="border border-border px-3 py-2 text-muted-foreground">{r.fields}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="section-2-2" eyebrow="2.2" title="Comment uploader un fichier">
        <AnnotatedScreen
          src="/tutorial/images/chapter2-upload.png"
          alt="Zone d'upload PDF annotée"
          annotations={[
            { x: 50, y: 30, label: "①", text: "Zone drag-and-drop : déposez votre PDF directement ici." },
            { x: 50, y: 55, label: "②", text: "Bouton Parcourir : alternative si le glisser-déposer ne marche pas." },
            { x: 15, y: 80, label: "③", text: "Sélecteur de type de document (obligatoire avant upload)." },
            { x: 85, y: 80, label: "④", text: "Score de fiabilité qui apparaîtra une fois l'analyse terminée." },
          ]}
        />
        <div className="space-y-4">
          <Step number={1} title="Sélectionnez le type de document">
            Choisissez dans la liste (Loi de Finances, Budget, etc.). Cela guide l'extracteur GPT-4o.
          </Step>
          <Step number={2} title="Glissez-déposez votre PDF">
            Ou cliquez pour parcourir. Un seul fichier à la fois.
          </Step>
          <Step number={3} title="Attendez l'extraction">
            L'analyse se déroule en arrière-plan : extraction texte (pdfplumber) puis 1 appel GPT-4o.
          </Step>
          <Step number={4} title="Vérifiez le statut">
            Le badge passe successivement par <em>Extraction</em> → <em>Analyse</em> → <em>Analysé</em>.
          </Step>
          <Step number={5} title="Reprise automatique">
            Si l'upload dépasse 90 s (gros PDF), l'interface continue à interroger le serveur jusqu'à la fin.
          </Step>
        </div>

        <InfoBox title="Limites techniques">
          Taille maximale : <strong>100 Mo par fichier</strong>. Jusqu'à{" "}
          <strong>5 fichiers par projet</strong>. Les PDF identiques (même
          empreinte SHA-256) ne déclenchent pas de nouvel appel LLM : leur
          extraction précédente est réutilisée instantanément.
        </InfoBox>
      </Section>

      <Section
        id="section-2-3"
        eyebrow="2.3"
        title="Comprendre le score de fiabilité"
      >
        <p>
          À chaque extraction, VITAE-PUBLICA™ calcule un{" "}
          <strong>score de fiabilité</strong> qui reflète la qualité de lecture
          du PDF par le modèle.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border-l-4 border-emerald-500 bg-emerald-500/10 p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              Excellent · 85 – 100 %
            </div>
            <p className="mt-1 text-xs">
              PDF textuel propre, structures tabulaires reconnues. Validation
              directe possible.
            </p>
          </div>
          <div className="rounded-lg border-l-4 border-amber-500 bg-amber-500/10 p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              Correct · 60 – 84 %
            </div>
            <p className="mt-1 text-xs">
              Lecture fiable mais certains chiffres peuvent nécessiter une
              relecture manuelle.
            </p>
          </div>
          <div className="rounded-lg border-l-4 border-red-500 bg-red-500/10 p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-300">
              Problématique · &lt; 60 %
            </div>
            <p className="mt-1 text-xs">
              Pages scannées, tableaux complexes, mise en page atypique.
              Correction manuelle indispensable.
            </p>
          </div>
        </div>

        <InfoBox title="PDF scannés et OCR">
          Un PDF scanné ne contient pas de texte sélectionnable. Avant upload,
          passez-le par un OCR (Adobe Acrobat, Tesseract, ABBYY) pour obtenir
          un PDF texte ré-utilisable.
        </InfoBox>
      </Section>

      <Section
        id="section-2-4"
        eyebrow="2.4"
        title="Valider et corriger les données extraites"
      >
        <AnnotatedScreen
          src="/tutorial/images/chapter2-validation.png"
          alt="Interface de revue des données extraites"
          annotations={[
            { x: 20, y: 15, label: "①", text: "Nom du document et type de fichier." },
            { x: 80, y: 15, label: "②", text: "Score de fiabilité global avec couleur." },
            { x: 50, y: 35, label: "③", text: "Champs pays / année / devise — vérifiez la cohérence." },
            { x: 50, y: 55, label: "④", text: "Macros nationales : PIB, population, croissance." },
            { x: 50, y: 75, label: "⑤", text: "Taux d'imposition extraits (IR, IS, TVA, cotisations)." },
            { x: 50, y: 92, label: "⑥", text: "Lignes budgétaires et emprunts récents." },
          ]}
        />
        <WarningBox title="Vérifiez TOUJOURS les 5 chiffres clés avant validation">
          Une erreur sur un seul de ces cinq chiffres invalide toute l'analyse
          vitaliste. Le modèle peut confondre « millions » et « milliards », ou
          inverser un pourcentage.
        </WarningBox>
        <ul className="ml-6 list-disc space-y-1 text-sm">
          <li>Recettes totales de l'exercice</li>
          <li>Dépenses totales de l'exercice</li>
          <li>Déficit ou excédent budgétaire</li>
          <li>Encours de dette publique</li>
          <li>Taux d'imposition global (IR + cotisations + TVA moyen)</li>
        </ul>
      </Section>

      <Section
        id="section-2-5"
        eyebrow="2.5"
        title="Uploader plusieurs documents"
      >
        <InfoBox title="Fusion intelligente multi-documents">
          Lorsque plusieurs documents coexistent dans un même projet (loi de
          finances + rapport cour des comptes + contrat d'emprunt), l'analyse
          vitaliste combine leurs données automatiquement. Les champs les plus
          précis priment : un chiffre tiré d'une loi votée l'emporte sur un
          chiffre tiré d'un rapport prévisionnel.
        </InfoBox>
        <TipBox title="Bon réflexe">
          Uploadez d'abord la loi de finances votée, puis ajoutez les
          documents complémentaires (rapports, emprunts). Vous gardez ainsi le
          contrôle de la hiérarchie des sources.
        </TipBox>
      </Section>

      <Section id="section-2-6" eyebrow="Validation" title="Quiz du chapitre">
        <Quiz
          question="Le score de fiabilité est à 48 %. Que faire ?"
          options={[
            "Valider quand même, l'analyse sera fiable",
            "Corriger manuellement les 5 chiffres clés avant de lancer l'analyse",
            "Supprimer le document et en chercher un autre",
          ]}
          correct={1}
          explanation="Un score < 60 % signale une extraction douteuse. La revue manuelle permet de sauver le document sans perdre le travail d'extraction déjà effectué."
        />
      </Section>
    </ChapterShell>
  );
}
